import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Alert, Badge, Button, Card, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { formatDateTime, formatDuration, type PanelTicket, statusTone, usePanel } from './data';
import { describeSla, shortSla, SlaBar } from './sla';
import { TakeButton } from './TakeButton';
import { PriorityLabel } from './TicketsPage';

/**
 * Vista principal del técnico (guía 7.1): su ticket activo bien visible, sus tickets por urgencia con el tiempo
 * restante y la cola que nadie tomó. Política: terminar el ticket en curso antes de tomar otro, salvo urgencia real.
 */
export function TechDashboard() {
  const panel = usePanel();
  if (!panel.data) return panel.error ? <Alert>{errorMessage(panel.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const { active, mine, queue, counts, warnPercent } = panel.data;

  return (
    <div className="space-y-5">
      <ActiveCard ticket={active} warnPercent={warnPercent} />

      <dl className="grid grid-cols-2 overflow-hidden rounded-md border border-slate-200 bg-white sm:grid-cols-5 sm:divide-x sm:divide-slate-200" aria-label={t('panel.resumen')}>
        <Counter label={t('panel.mis_abiertos')} value={counts.mine} />
        <Counter label={t('panel.por_vencer', { pct: warnPercent })} value={counts.dueSoon} tone={counts.dueSoon > 0 ? 'amber' : undefined} />
        <Counter label={t('panel.vencidos')} value={counts.overdue} tone={counts.overdue > 0 ? 'red' : undefined} />
        <Counter label={t('panel.en_pausa')} value={counts.paused} />
        <Counter label={t('panel.sin_responsable')} value={counts.queue} tone={counts.queue > 0 ? 'amber' : undefined} />
      </dl>

      <div className="grid gap-5 xl:grid-cols-2">
        <Card title={t('panel.mis_tickets')}>
          {mine.length === 0 ? (
            <p className="text-sm text-slate-500">{t('panel.mis_tickets_vacio')}</p>
          ) : (
            <TicketRows tickets={mine} label={t('panel.mis_tickets')} warnPercent={warnPercent} />
          )}
        </Card>
        <Card title={t('panel.cola')}>
          {queue.length === 0 ? (
            <p className="text-sm text-slate-500">{t('panel.cola_vacia')}</p>
          ) : (
            <TicketRows tickets={queue} label={t('panel.cola')} warnPercent={warnPercent} showAge />
          )}
        </Card>
      </div>
    </div>
  );
}

/** Un contador del resumen: el número toma color solo cuando hay algo que atender. */
function Counter({ label, value, tone }: { label: string; value: number; tone?: 'amber' | 'red' }) {
  const color = tone === 'red' ? 'text-red-700' : tone === 'amber' ? 'text-amber-700' : 'text-tinta';
  return (
    <div className="flex flex-col-reverse gap-1 border-b border-slate-200 px-4 py-3 sm:border-b-0">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={`text-3xl font-semibold leading-none tabular-nums ${color}`}>{value}</dd>
    </div>
  );
}

/** El ticket en el que trabaja, con el tiempo que le queda en grande. Es lo primero que ve el técnico. */
function ActiveCard({ ticket, warnPercent }: { ticket: PanelTicket | null; warnPercent: number }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!ticket) {
    return (
      <section aria-label={t('panel.activo')} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-dashed border-slate-300 px-5 py-4">
        <h2 className="text-sm font-semibold text-slate-700">{t('panel.activo')}</h2>
        <p className="text-sm text-slate-600">{t('panel.sin_activo')}</p>
      </section>
    );
  }

  const { main, detail, late } = describeSla(ticket.sla);
  const running = ticket.sla.state === 'en_curso' && ticket.sla.remainingMs !== null;
  const release = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/tickets/${ticket.number}/activo`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: ['tickets'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label={t('panel.activo')} className={`overflow-hidden rounded-md bg-tinta text-white shadow-[inset_4px_0_0_var(--color-senal)] ${late ? 'shadow-[inset_4px_0_0_var(--color-red-500)]' : ''}`}>
      <div className="grid gap-x-8 gap-y-4 px-6 py-5 lg:grid-cols-[1fr_auto]">
        <div className="min-w-0">
          <h2 className="text-sm font-medium text-senal">{t('panel.activo')}</h2>
          <Link to={`/tickets/${ticket.number}`} className="mt-1 block text-xl font-semibold leading-snug hover:underline">
            <span className="mr-2 font-mono text-sm font-normal text-white/60">{ticket.code}</span>
            {ticket.title}
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/75">
            <Badge tone={statusTone(ticket.statusCategory)}>{ticket.status}</Badge>
            {ticket.priority && <PriorityLabel name={ticket.priority} color={ticket.priorityColor} />}
            {ticket.requester && <span>{ticket.requester}</span>}
            {ticket.activeSince && <span>{t('panel.activo_desde', { fecha: formatDateTime(ticket.activeSince) })}</span>}
          </div>
        </div>
        <div className="lg:text-right">
          {running ? (
            <>
              <p className={`text-4xl font-semibold leading-none tabular-nums ${late ? 'text-red-300' : 'text-white'}`}>{formatDuration(Math.abs(ticket.sla.remainingMs!))}</p>
              <p className="mt-1.5 text-sm text-white/75">{main}</p>
            </>
          ) : (
            <p className={`text-base font-semibold ${late ? 'text-red-300' : 'text-white'}`}>{main}</p>
          )}
          {detail && <p className="mt-0.5 text-xs text-white/60">{detail}</p>}
        </div>
      </div>
      <div className="px-6">
        <SlaBar sla={ticket.sla} warnPercent={warnPercent} />
      </div>
      {error && (
        <div className="px-6 pt-3">
          <Alert>{error}</Alert>
        </div>
      )}
      <div className="flex flex-wrap gap-2 px-6 pb-5 pt-4">
        <Link to={`/tickets/${ticket.number}`} className="rounded bg-senal px-4 py-2 text-sm font-semibold text-tinta hover:bg-amber-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
          {t('panel.abrir')}
        </Link>
        <Button variant="inverse" disabled={busy} onClick={() => void release()}>
          {t('tk.soltar')}
        </Button>
      </div>
    </section>
  );
}

function TicketRows({ tickets, label, warnPercent, showAge = false }: { tickets: PanelTicket[]; label: string; warnPercent: number; showAge?: boolean }) {
  return (
    <ul className="-mx-2 space-y-1" aria-label={label}>
      {tickets.map((tk) => {
        const late = tk.sla.state === 'en_curso' && (tk.sla.remainingMs ?? 0) < 0;
        const soon = !late && tk.sla.state === 'en_curso' && (tk.sla.ratio ?? 0) * 100 >= warnPercent;
        return (
          // El borde izquierdo lleva el color de la prioridad: lo urgente se ve sin leer.
          <li key={tk.id} className="flex items-center justify-between gap-3 rounded-r border-l-[3px] py-2 pl-3 pr-2 hover:bg-slate-50" style={{ borderLeftColor: tk.priorityColor ?? 'var(--color-slate-200)' }}>
            <div className="min-w-0 text-sm">
              <Link to={`/tickets/${tk.number}`} className="font-medium text-slate-900 hover:underline">
                <span className="mr-2 font-mono text-xs font-normal text-slate-500">{tk.code}</span>
                {tk.title}
              </Link>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                <span className={late ? 'font-medium text-red-700' : soon ? 'font-medium text-amber-700' : ''}>{shortSla(tk.sla)}</span>
                {tk.priority && <PriorityLabel name={tk.priority} color={tk.priorityColor} />}
                {tk.requester && <span>{tk.requester}</span>}
                {showAge && <span>{t('panel.entro', { fecha: formatDateTime(tk.createdAt) })}</span>}
              </div>
            </div>
            <TakeButton number={tk.number} code={tk.code} className="shrink-0" />
          </li>
        );
      })}
    </ul>
  );
}
